if (!customElements.get('newsletter-modal')) {
  customElements.define(
    'newsletter-modal',
    class NewsletterModal extends ModalElement {
      constructor() {
        super();
    
        // Prevent popup on Shopify robot challenge page
        if (window.location.pathname === '/challenge' || !theme.cookiesEnabled) {
          return;
        }
    
        if (!theme.config.isTouch || Shopify.designMode) {
          this.init();
        }
        else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
      }

      connectedCallback() {
        super.connectedCallback();
        this.setupForm();
      }
    
      get shouldLock() {
        return true;
      }
    
      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }
    
      get delay() {
        return this.hasAttribute('data-delay') ? parseInt(this.getAttribute('data-delay')) : 5;
      }
    
      get expiry() {
        return this.hasAttribute('data-expiry') ? parseInt(this.getAttribute('data-expiry')) : 30;
      }

      // Blog articles: percentage of the page to read before the popup opens.
      get scrollDepth() {
        return this.hasAttribute('data-scroll') ? parseInt(this.getAttribute('data-scroll')) : 0;
      }
    
      get cookieName() {
        return 'concept:newsletter-popup';
      }
    
      get submited() {
        return this.querySelector('.alert') !== null;
      }

      get successPanel() {
        return this.querySelector('[data-newsletter-success]');
      }
    
      init() {
        if (this.initialized) return;
        this.initialized = true;
        
        // Open modal if errors or success message exist
        if (this.submited) {
          if (this.querySelector('.alert--success')) this.showSuccess();
          this.load(1);
          return;
        }
    
        if (this.testMode || !this.getCookie(this.cookieName)) {
          this.load(this.delay);
        }
      }

      setupForm() {
        const form = this.querySelector('form.newsletter-form--popup');
        if (!form || this.form) return;

        this.form = form;
        this.submitButton = form.querySelector('button[type="submit"]');

        // On phones the first tap on the button closes the keyboard, the sheet
        // drops with it and the click lands on nothing. Submitting on the press,
        // before the keyboard moves anything, makes one tap enough.
        this.submitButton?.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse') return;
          event.preventDefault();
          this.pressedAt = Date.now();
          if (form.requestSubmit) form.requestSubmit(this.submitButton);
          else this.submitButton.click();
        });

        this.submitButton?.addEventListener('click', (event) => {
          if (this.pressedAt && Date.now() - this.pressedAt < 1000) event.preventDefault();
        });

        form.addEventListener('submit', (event) => {
          event.preventDefault();
          if (this.submitting) return;

          // Shopify's invisible hCaptcha takes the submit, adds its token and
          // then calls form.submit(), which lands in ajaxSubmit below. The timer
          // covers the case where it never does.
          if (this.hasCaptcha()) {
            clearTimeout(this.captchaTimer);
            this.captchaTimer = setTimeout(() => this.ajaxSubmit(), 3000);
            return;
          }

          this.ajaxSubmit();
        });

        form.submit = () => {
          clearTimeout(this.captchaTimer);
          this.ajaxSubmit();
        };

        const copyButton = this.querySelector('[data-copy-code]');
        copyButton?.addEventListener('click', () => this.copyCode(copyButton));
      }

      hasCaptcha() {
        return !!this.form.querySelector('.h-captcha, [data-hcaptcha-widget-id], iframe[src*="hcaptcha"]');
      }

      async ajaxSubmit() {
        if (this.submitting) return;

        const form = this.form;
        this.submitting = true;
        this.setLoading(true);

        try {
          const response = await fetch(form.getAttribute('action') || '/contact', {
            method: 'POST',
            body: new FormData(form),
            headers: { Accept: 'text/html' }
          });

          if (response.url.includes('/challenge')) {
            window.location.href = response.url;
            return;
          }

          if (!response.ok) throw new Error(`Newsletter signup failed: ${response.status}`);

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const error = doc.getElementById(form.id)?.querySelector('.alert--error');

          if (error) {
            this.showError(error);
            return;
          }

          this.showSuccess();
        }
        catch (error) {
          console.error(error);
          HTMLFormElement.prototype.submit.call(form);
        }
        finally {
          this.submitting = false;
          this.setLoading(false);
        }
      }

      setLoading(loading) {
        if (!this.submitButton) return;
        this.submitButton.toggleAttribute('aria-busy', loading);
        this.form.classList.toggle('is-loading', loading);
      }

      showError(alert) {
        this.form.querySelectorAll('.alert').forEach((element) => element.remove());
        const node = document.importNode(alert, true);
        node.setAttribute('role', 'alert');
        this.form.append(node);
      }

      showSuccess() {
        this.subscribed = true;
        if (!this.testMode) this.setCookie(this.cookieName, 365);

        const panel = this.successPanel;
        if (!panel) return;

        this.querySelector('.drawer__scrollable > .rich-text')?.setAttribute('hidden', '');
        panel.hidden = false;
        document.activeElement?.blur();
        panel.focus({ preventScroll: true });

        this.applyCode();
      }

      // Shopify's discount link stores the code in a cookie that checkout reads,
      // so the visitor never has to type it. The redirect target is the lightest
      // page there is.
      applyCode() {
        const code = this.querySelector('[data-copy-code]')?.dataset.copyCode;
        if (!code || this.testMode) return;

        fetch(`/discount/${encodeURIComponent(code)}?redirect=/cart.js`, { credentials: 'same-origin' })
          .catch((error) => console.error(error));
      }

      async copyCode(button) {
        const label = button.querySelector('[data-copy-label]');
        try {
          await navigator.clipboard.writeText(button.dataset.copyCode);
          if (label) {
            label.dataset.label = label.dataset.label || label.textContent;
            label.textContent = label.dataset.copied;
            setTimeout(() => { label.textContent = label.dataset.label; }, 2000);
          }
        }
        catch (error) {
          window.getSelection()?.selectAllChildren(button.querySelector('.newsletter-success__code-value'));
        }
      }

      load(delay) {
        if (Shopify.designMode) return;

        if (this.scrollDepth > 0 && !this.submited) {
          this.waitForScroll();
          return;
        }

        this.revealAt = Date.now() + delay * 1000;
        setTimeout(() => this.tryShow(), delay * 1000);
      }

      waitForScroll() {
        const onScroll = theme.utils.throttle(() => {
          const scrollable = document.documentElement.scrollHeight - window.innerHeight;
          if (scrollable > 0 && (window.scrollY / scrollable) * 100 < this.scrollDepth) return;

          window.removeEventListener('scroll', onScroll);
          this.revealAt = Date.now();
          this.tryShow();
        });

        window.addEventListener('scroll', onScroll, { passive: true });
      }

      // 'modal' while a drawer or dialog is open, 'cookie' while the cookie
      // banner waits for an answer.
      blocker() {
        if (document.body.classList.contains('has-modal-open')) return 'modal';
        if (document.body.classList.contains('has-modal-opening')) return 'modal';

        const cookieBanner = document.getElementById('CookieBanner');
        if (cookieBanner && (cookieBanner.hasAttribute('open') || cookieBanner.hasAttribute('active'))) {
          return 'cookie';
        }

        return null;
      }

      tryShow() {
        if (this.open) return;

        // The cookie banner only delays the popup: it opens once the visitor
        // has answered. A drawer they kept open for long means they are busy
        // shopping, so the popup gives up after 45 s.
        const blocker = this.blocker();
        if (blocker) {
          if (blocker === 'modal' && Date.now() - this.revealAt > 45000) return;
          setTimeout(() => this.tryShow(), 1500);
          return;
        }

        this.show();
      }
    
      afterShow() {
        super.afterShow();
        this.classList.add('show-image');
      }
    
      afterHide() {
        super.afterHide();
        this.classList.remove('show-image');
    
        // Remove a cookie in case it was set in test mode
        if (this.testMode) {
          this.removeCookie(this.cookieName);
          return;
        }
    
        this.setCookie(this.cookieName, this.subscribed ? 365 : this.expiry);
      }
    
      getCookie(name) {
        const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
        return match ? match[2] : null;
      }
    
      setCookie(name, expiry) {
        document.cookie = `${name}=true; max-age=${(expiry * 24 * 60 * 60)}; path=/`;
      }
    
      removeCookie(name) {
        document.cookie = `${name}=; max-age=0`;
      }
    }
  );
}
